import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Grid, Table, TableBody, TableCell,
  TableHead, TableRow, IconButton, Tooltip, Alert, Divider,
} from '@mui/material';
import { Home, Logout, Print } from '@mui/icons-material';
import axios from 'axios';
import CompanyHeader from './CompanyHeader';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

const Stat = ({ value, label, color }) => (
  <Card elevation={2} sx={{ borderRadius: 3, borderLeft: `4px solid ${color}` }}>
    <CardContent sx={{ py: 1.8 }}>
      <Typography variant="h4" fontWeight={800} color={color} lineHeight={1}>{value}</Typography>
      <Typography variant="body2" fontWeight={600} mt={0.5}>{label}</Typography>
    </CardContent>
  </Card>
);

const ExitReports = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [interviews, setInterviews] = useState([]);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [s, i] = await Promise.all([
        axios.get(`${API}/api/exit/reports/summary`, { headers: hdrs() }),
        axios.get(`${API}/api/exit/reports/exit-interviews`, { headers: hdrs() }),
      ]);
      setSummary(s.data); setInterviews(i.data);
    } catch (e) { setError(e.response?.data?.error || 'Failed to load reports'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4, mb: 4, px: 2 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1} className="no-print">
            <Box display="flex" alignItems="center" gap={1}><Logout color="primary" /><Typography variant="h5" color="primary">Separation Reports</Typography></Box>
            <Box display="flex" gap={1}>
              <Tooltip title="Print"><IconButton onClick={() => window.print()}><Print /></IconButton></Tooltip>
              <Tooltip title="Separations"><IconButton onClick={() => navigate('/hr/exit')}><Home /></IconButton></Tooltip>
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <CompanyHeader title="Separation &amp; Exit Report" />

          {summary && (
            <>
              <Grid container spacing={2} mb={3}>
                <Grid item xs={6} sm={3}><Stat value={summary.total} label="Total Separations" color="#1565c0" /></Grid>
                <Grid item xs={6} sm={3}><Stat value={summary.released} label="Released" color="#2e7d32" /></Grid>
                <Grid item xs={6} sm={3}><Stat value={summary.inProgress} label="In Progress" color="#e65100" /></Grid>
                <Grid item xs={6} sm={3}><Stat value={`${summary.turnoverRate}%`} label="Turnover Rate" color="#37474f" /></Grid>
              </Grid>

              <Typography variant="subtitle2" fontWeight={700} mb={1}>Separations by Category</Typography>
              <Table size="small" sx={{ mb: 3 }}>
                <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}><TableCell><strong>Type</strong></TableCell><TableCell align="center"><strong>Count</strong></TableCell></TableRow></TableHead>
                <TableBody>
                  {(summary.byType || []).length === 0 ? <TableRow><TableCell colSpan={2} align="center"><Typography variant="caption" color="text.secondary">No data</Typography></TableCell></TableRow>
                    : summary.byType.map(t => <TableRow key={t.type}><TableCell>{t.type}</TableCell><TableCell align="center">{t.count}</TableCell></TableRow>)}
                </TableBody>
              </Table>
            </>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" fontWeight={700} mb={1.5}>Exit Interview Reports</Typography>
          {interviews.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No exit interviews submitted yet.</Typography>
          ) : (
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Employee</strong></TableCell><TableCell><strong>Dept</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell><TableCell><strong>Reason for Leaving</strong></TableCell>
                <TableCell><strong>Recommend?</strong></TableCell>
              </TableRow></TableHead>
              <TableBody>
                {interviews.map(s => (
                  <TableRow key={s._id}>
                    <TableCell>{s.employeeName}<Typography variant="caption" color="text.secondary" display="block">{fmtDate(s.exitInterview?.submittedDate)}</Typography></TableCell>
                    <TableCell>{s.department || '—'}</TableCell>
                    <TableCell>{s.type}</TableCell>
                    <TableCell><Typography variant="caption">{s.exitInterview?.reasonForLeaving || '—'}</Typography></TableCell>
                    <TableCell>{s.exitInterview?.wouldRecommend || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ExitReports;
